import * as XLSX from 'xlsx';

/**
 * Helper function to get value from row with multiple possible column names
 */
const getFieldValue = (row, ...possibleNames) => {
  for (const name of possibleNames) {
    if (row[name] !== undefined && row[name] !== null && row[name] !== '') {
      return row[name];
    }
  }
  return null;
};

/**
 * Parse Excel or CSV file and convert to farmer objects
 * @param {File} file - The Excel or CSV file to parse
 * @returns {Promise<Array>} Array of farmer objects
 */
export const parseExcelFile = async (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        
        // Get the first sheet
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        
        // Convert to JSON - this preserves column headers exactly as they are
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { defval: null });
        // Transform Excel data to farmer objects
        const farmers = jsonData.map((row, index) => {
          // Parse cacao varieties from "Variety Planted" column
          let cacaoVarieties = [];
          const varietyPlanted = getFieldValue(
            row,
            'Variety Planted',
            'VarietyPlanted',
            'Variety_Planted',
            'variety_planted',
            'varieties'
          );
          
          if (varietyPlanted && typeof varietyPlanted === 'string') {
            cacaoVarieties = varietyPlanted.split(',').map(v => v.trim()).filter(Boolean);
          }
          
          // Parse clones if needed
          const clones = getFieldValue(row, 'Clones', 'clones', 'Clone');
          if (clones && clones.includes('Brazilian')) {
            if (!cacaoVarieties.includes('Brazilian')) {
              cacaoVarieties.push('Brazilian');
            }
          }
          
          // Build full name with flexible column matching
          const firstname = getFieldValue(row, 'Firstname', 'firstname', 'first_name', 'firstName', 'first');
          const lastname = getFieldValue(row, 'Lastname', 'lastname', 'last_name', 'lastName', 'last');
          const middleInitial = getFieldValue(row, 'Middle Initial', 'Middle_Initial', 'middleInitial', 'middle_initial', 'MI');
          const fullName = `${firstname || ''} ${middleInitial || ''} ${lastname || ''}`.replace(/\s+/g, ' ').trim();
          
          // Build address
          const sitio = getFieldValue(row, 'Sitio/Purok', 'Sitio_Purok', 'sitioPurok', 'Sitio', 'sitio', 'Purok', 'purok');
          const barangay = getFieldValue(row, 'Barangay', 'barangay', 'Baranggay', 'village', 'Village');
          const municipality = getFieldValue(row, 'Municipality', 'municipality', 'Municipal', 'City', 'city');
          const address = [sitio, barangay, municipality].filter(Boolean).join(', ');

          const farmerData = {
            // Basic identification
            profile_id: getFieldValue(row, 'profile_id', 'profileId', 'ID', 'id', 'Id', 'Farmer_ID', 'farmer_id', 'FarmerID') || index + 1,
            Firstname: firstname,
            Lastname: lastname,
            Middle_Initial: middleInitial,
            name: fullName || 'Unknown',
            displayName: fullName || 'Unknown',
            
            // Personal info
            Respondent: getFieldValue(row, 'Respondent', 'respondent'),
            Age: getFieldValue(row, 'Age', 'age', 'AGE'),
            Gender: getFieldValue(row, 'Gender', 'gender', 'sex', 'Sex'),
            Civil_Status: getFieldValue(row, 'Civil Status', 'Civil_Status', 'civilStatus', 'civil_status'),
            Educational_Attainment: getFieldValue(row, 'Educational Attainment', 'Educational_Attainment', 'educationalAttainment', 'Education'),
            No_of_Household_Members: getFieldValue(row, 'No. of Household Members', 'No_of_Household_Members', 'noOfHouseholdMembers'),
            Occupation: getFieldValue(row, 'Occupation', 'occupation'),
            
            // Location
            Sitio_Purok: sitio,
            Barangay: barangay,
            Municipality: municipality,
            address: address,
            
            // Income
            Annual_Farm_Income_from_Cacao: getFieldValue(row, 'Annual Farm Income from Cacao', 'Annual_Farm_Income_from_Cacao', 'Annual Farm Income', 'annualFarmIncome'),
            Form_of_Income: getFieldValue(row, 'Form of Income', 'Form_of_Income', 'formOfIncome'),
            In_Peso_Income: getFieldValue(row, 'In Peso Income', 'In_Peso_Income', 'inPesoIncome'),
            In_Kind_Income: getFieldValue(row, 'In Kind Income', 'In_Kind_Income', 'inKindIncome'),
            
            // Membership
            Affiliation_Name_of_Organization_Cooperative: getFieldValue(
              row,
              'Affiliation (Name of Organization/Cooperative)',
              'Affiliation_Name_of_Organization_Cooperative',
              'Affiliation',
              'affiliation'
            ),
            Years_in_Membership: getFieldValue(row, 'Years in Membership', 'Years_in_Membership', 'yearsInMembership'),
            Active: getFieldValue(row, 'Active', 'active'),
            POSITION: getFieldValue(row, 'Position', 'position', 'POSITION'),
            Designation: getFieldValue(row, 'Designation', 'designation'),
            
            // Farm details
            Farm_Area_ha: getFieldValue(row, 'Farm Area (ha)', 'Farm_Area_ha', 'Farm Area', 'farmArea', 'farm_area'),
            farmSize: getFieldValue(row, 'Farm Area (ha)', 'Farm_Area_ha', 'Farm Area', 'farmArea', 'farm_area'),
            Year_Established: getFieldValue(row, 'Year Established', 'Year_Established', 'yearEstablished'),
            Actual_Number_of_Trees_Planted: getFieldValue(row, 'Actual Number of Trees Planted', 'Actual_Number_of_Trees_Planted', 'Actual Number of Trees', 'actualTrees'),
            No_of_Productive_Trees_Before_Typhoon: getFieldValue(row, 'No of Productive Trees (Before Typhoon)', 'No_of_Productive_Trees_Before_Typhoon', 'productiveTreesBefore'),
            No_of_Productive_Trees_After_Typhoon: getFieldValue(row, 'No of Productive Trees (After Typhoon)', 'No_of_Productive_Trees_After_Typhoon', 'productiveTreesAfter'),
            Source_Provider: getFieldValue(row, 'Source/Provider', 'Source_Provider', 'sourceProvider'),
            Propagation_Technique: getFieldValue(row, 'Propagation Technique', 'Propagation_Technique', 'propagationTechnique'),
            Land_Ownership: getFieldValue(row, 'Land Ownership', 'Land_Ownership', 'landOwnership'),
            landOwnership: getFieldValue(row, 'Land Ownership', 'Land_Ownership', 'landOwnership'),
            Variety_Planted: varietyPlanted,
            cacaoVarieties: cacaoVarieties,
            Clones: clones,
            Availability_of_Nursery: getFieldValue(row, 'Availability  of Nursery', 'Availability of Nursery', 'Availability_of_Nursery', 'availabilityOfNursery'),
            
            // Productivity
            Productivity_pods_tree_year: getFieldValue(row, 'Productivity (pods/tree-year)', 'Productivity_pods_tree_year', 'productivity'),
            Pods_per_tree_harvest: getFieldValue(row, 'Pods per tree/harvest', 'Pods_per_tree_harvest', 'podsPerTreeHarvest'),
            Weight_of_beans_per_harvest_kg: getFieldValue(row, 'weight of beans per harvest (kg)', 'Weight_of_beans_per_harvest_kg', 'weightOfBeansPerHarvest'),
            Weight_of_beans_per_hectare_kg: getFieldValue(row, 'weight of beans per hectare (kg)', 'Weight_of_beans_per_hectare_kg', 'weightOfBeansPerHectare'),
            
            // Knowledge
            Knowledge_on_Cacao_Farming_Trainings_Seminar_Attended: getFieldValue(row, 'Knowledge on Cacao Farming (Trainings/Seminar Attended)', 'Knowledge_on_Cacao_Farming_Trainings_Seminar_Attended', 'trainings'),
            Production: getFieldValue(row, 'Production', 'production'),
          };
          return farmerData;
        });
        resolve(farmers);
      } catch (error) {
        reject(new Error(`Failed to parse Excel file: ${error.message}`));
      }
    };
    
    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };
    
    reader.readAsArrayBuffer(file);
  });
};

/**
 * Transform parsed Excel farmer data to backend API format
 * Matches the structure used by AddFarmerForm
 */
export const transformToAPIFormat = (farmerData) => {
  const parseNumber = (val) => {
    if (val === null || val === undefined || val === '') return null;
    const num = parseFloat(val);
    return isNaN(num) ? null : num;
  };

  const parseInt = (val) => {
    if (val === null || val === undefined || val === '') return null;
    const num = Number(val);
    return isNaN(num) ? null : Math.floor(num);
  };

  // Build nested objects only if they contain data
  const buildIncome = () => {
    const obj = {
      annualFarmIncome: farmerData.Annual_Farm_Income_from_Cacao || null,
      formOfIncome: farmerData.Form_of_Income || null,
      inPesoIncome: farmerData.In_Peso_Income || null,
      inKindIncome: farmerData.In_Kind_Income || null
    };
    const hasData = obj.annualFarmIncome || obj.formOfIncome || obj.inPesoIncome || obj.inKindIncome;
    return hasData ? obj : null;
  };

  const buildMembership = () => {
    const obj = {
      affiliationName: farmerData.Affiliation_Name_of_Organization_Cooperative || null,
      yearsInMembership: parseInt(farmerData.Years_in_Membership),
      active: farmerData.Active || null,
      position: farmerData.POSITION || null,
      designation: farmerData.Designation || null
    };
    const hasData = obj.affiliationName || obj.yearsInMembership !== null || obj.active || obj.position || obj.designation;
    return hasData ? obj : null;
  };

  const buildFarm = () => {
    const obj = {
      farmArea: parseNumber(farmerData.Farm_Area_ha),
      yearEstablished: parseInt(farmerData.Year_Established),
      actualTrees: parseInt(farmerData.Actual_Number_of_Trees_Planted),
      productiveTreesBefore: parseInt(farmerData.No_of_Productive_Trees_Before_Typhoon),
      productiveTreesAfter: parseInt(farmerData.No_of_Productive_Trees_After_Typhoon),
      sourceProvider: farmerData.Source_Provider || null,
      propagationTechnique: farmerData.Propagation_Technique || null,
      landOwnership: farmerData.Land_Ownership || null,
      varietyPlanted: farmerData.Variety_Planted || (farmerData.cacaoVarieties && farmerData.cacaoVarieties.length > 0 ? farmerData.cacaoVarieties.join(', ') : null),
      clones: farmerData.Clones || null,
      availabilityOfNursery: farmerData.Availability_of_Nursery || null
    };
    const hasData = obj.farmArea !== null || obj.yearEstablished !== null || obj.actualTrees !== null || 
                    obj.productiveTreesBefore !== null || obj.productiveTreesAfter !== null || 
                    obj.sourceProvider || obj.propagationTechnique || obj.landOwnership || 
                    obj.varietyPlanted || obj.clones || obj.availabilityOfNursery;
    return hasData ? obj : null;
  };

  const buildProductivity = () => {
    const obj = {
      productivityPodsTreeYear: parseNumber(farmerData.Productivity_pods_tree_year),
      podsPerTreeHarvest: parseNumber(farmerData.Pods_per_tree_harvest),
      weightOfBeansPerHarvestKg: parseNumber(farmerData.Weight_of_beans_per_harvest_kg),
      weightOfBeansPerHectareKg: parseNumber(farmerData.Weight_of_beans_per_hectare_kg)
    };
    const hasData = obj.productivityPodsTreeYear !== null || obj.podsPerTreeHarvest !== null || 
                    obj.weightOfBeansPerHarvestKg !== null || obj.weightOfBeansPerHectareKg !== null;
    return hasData ? obj : null;
  };

  const buildKnowledge = () => {
    const obj = {
      knowledgeOnCacaoFarming: farmerData.Knowledge_on_Cacao_Farming_Trainings_Seminar_Attended || null,
      production: farmerData.Production || null
    };
    const hasData = obj.knowledgeOnCacaoFarming || obj.production;
    return hasData ? obj : null;
  };

  const incomePayload = buildIncome();
  const membershipPayload = buildMembership();
  const farmPayload = buildFarm();
  const productivityPayload = buildProductivity();
  const knowledgePayload = buildKnowledge();

  // Build main payload matching AddFarmerForm structure
  const payload = {
    lastname: farmerData.Lastname || null,
    firstname: farmerData.Firstname || null,
    middleInitial: farmerData.Middle_Initial || null,
    respondent: farmerData.Respondent || null,
    sitioPurok: farmerData.Sitio_Purok || null,
    barangay: farmerData.Barangay || null,
    municipality: farmerData.Municipality || null,
    age: parseInt(farmerData.Age),
    gender: farmerData.Gender || null,
    civilStatus: farmerData.Civil_Status || null,
    education: farmerData.Educational_Attainment || null,
    household: parseInt(farmerData.No_of_Household_Members),
    occupation: farmerData.Occupation || null,
  };

  // Only add nested objects if they contain data
  if (incomePayload) payload.incomeData = incomePayload;
  if (membershipPayload) payload.membershipData = membershipPayload;
  if (farmPayload) payload.farmData = farmPayload;
  if (productivityPayload) payload.productivityData = productivityPayload;
  if (knowledgePayload) payload.knowledgeData = knowledgePayload;

  return payload;
};

/**
 * Export farmers data to Excel file
 * @param {Array} farmers - Array of farmer objects
 * @param {string} filename - Name for the exported file
 */
export const exportToExcel = (farmers, filename = 'farmers_export.xlsx') => {
  // Transform farmer objects to Excel-friendly format
  const excelData = farmers.map(farmer => {
    const raw = farmer._raw || farmer;
    
    return {
      'ID': farmer.id || raw.Farmer_ID,
      'Lastname': raw.Lastname || farmer.name?.split(' ').pop() || '',
      'Firstname': raw.Firstname || farmer.name?.split(' ')[0] || '',
      'Middle Initial': raw.Middle_Initial || '',
      'Respondent': raw.Respondent || '',
      'Sitio/Purok': farmer.sitio || raw.Sitio_Purok || '',
      'Barangay': farmer.barangay || raw.Barangay || '',
      'Municipality': farmer.municipality || raw.Municipality || '',
      'Age': farmer.age || raw.Age || '',
      'Gender': farmer.gender || raw.Gender || '',
      'Civil Status': raw.Civil_Status || '',
      'Educational Attainment': raw.Educational_Attainment || '',
      'No. of Household Members': raw.No_of_Household_Members || '',
      'Annual Farm Income from Cacao': raw.Annual_Farm_Income_from_Cacao || '',
      'Form of Income': raw.Form_of_Income || '',
      'In Peso Income': raw.In_Peso_Income || '',
      'In Kind Income': raw.In_Kind_Income || '',
      'Occupation': raw.Occupation || '',
      'Affiliation (Name of Organization/Cooperative)': raw.Affiliation_Name_of_Organization_Cooperative || '',
      'Years in Membership': raw.Years_in_Membership || '',
      'Active': raw.Active || '',
      'Position': raw.POSITION || '',
      'Designation': raw.Designation || '',
      'Farm Area (ha)': farmer.farmSize || raw.Farm_Area_ha || '',
      'Year Established': raw.Year_Established || '',
      'Actual Number of Trees Planted': raw.Actual_Number_of_Trees_Planted || '',
      'No of Productive Trees (Before Typhoon)': raw.No_of_Productive_Trees_Before_Typhoon || '',
      'No of Productive Trees (After Typhoon)': raw.No_of_Productive_Trees_After_Typhoon || '',
      'Source/Provider': raw.Source_Provider || '',
      'Propagation Technique': raw.Propagation_Technique || '',
      'Land Ownership': farmer.landOwnership || raw.Land_Ownership || '',
      'Variety Planted': farmer.cacaoVarieties?.join(', ') || raw.Variety_Planted || '',
      'Clones': raw.Clones || '',
      'Availability  of Nursery': raw.Availability_of_Nursery || '',
      'Productivity (pods/tree-year)': raw.Productivity_pods_tree_year || '',
      'Pods per tree/harvest': raw.Pods_per_tree_harvest || '',
      'weight of beans per harvest (kg)': raw.Weight_of_beans_per_harvest_kg || '',
      'weight of beans per hectare (kg)': raw.Weight_of_beans_per_hectare_kg || '',
      'Knowledge on Cacao Farming (Trainings/Seminar Attended)': raw.Knowledge_on_Cacao_Farming_Trainings_Seminar_Attended || '',
      'Production': raw.Production || '',
    };
  });
  
  // Create worksheet
  const worksheet = XLSX.utils.json_to_sheet(excelData);
  
  // Create workbook
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Farmers');
  
  // Save file
  XLSX.writeFile(workbook, filename);
};
